import type { MembershipRole } from '@/api/types'

/**
 * Whether the invite form offers a zone choice for this role (§16).
 *
 * An ADMIN reaches every zone in the account from the membership alone and never holds a
 * `zone_members` row, so there is nothing to choose. This is the same rule the server enforces —
 * it ignores `zone_ids` for an ADMIN — rather than a second opinion about zone reach held in the
 * console.
 */
export function choosesZones(role: MembershipRole): boolean {
  return role === 'USER'
}

/**
 * The `zone_ids` to put on the invitation.
 *
 * Ticks are kept in component state while the admin toggles between roles, so switching to ADMIN
 * and back does not silently lose the selection. **What must not survive that round trip is the
 * SEND**: an ADMIN's invitation carries `[]` no matter what is ticked, so a redundant grant is
 * never written — one that would say nothing today and would outlive a later demotion, handing the
 * demoted person a zone set nobody chose.
 */
export function zoneIdsFor(role: MembershipRole, ticked: string[]): string[] {
  return choosesZones(role) ? ticked : []
}

/**
 * The account members a zone's access page lists (bug-221): everyone but BILLING members, who have
 * no zone access at all — the server refuses a grant (409 `billing_has_no_zones`), so offering
 * "Grant access" for them is a button that can only fail.
 */
export function zoneGrantCandidates<T extends { role: MembershipRole }>(users: readonly T[]): T[] {
  return users.filter((user) => user.role !== 'BILLING')
}
